import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import BrandLogo from "@/components/BrandLogo";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import {
  CalendarCheck, Wallet, BookOpenCheck, FileSpreadsheet, CalendarDays,
  Megaphone, Sparkles, GraduationCap, Shield, Users, ArrowRight,
  Check, Star, Quote, Zap, ChevronRight, MapPin,
} from "lucide-react";
import { cn } from "@/lib/utils";

const FEATURES = [
  { icon: CalendarCheck, title: "Attendance", body: "Roll call in seconds. Auto-notify parents when a learner is absent. Term-by-term insights per class and per child." },
  { icon: Wallet, title: "Fees & invoicing", body: "Term invoices in ZAR, part-payments, receipts, arrears reports. Paystack-ready when you flip the switch." },
  { icon: BookOpenCheck, title: "Homework", body: "Teachers set. Learners submit. Parents see. AI hints help learners who are stuck — without doing the work for them." },
  { icon: FileSpreadsheet, title: "Exams & reports", body: "Enter marks in a spreadsheet-style grid. Generate CAPS-aligned report cards as PDFs, one click, whole class." },
  { icon: CalendarDays, title: "Timetable", body: "Visual grid editor. Assign subjects and teachers per period. Every learner sees their day's schedule instantly." },
  { icon: Megaphone, title: "Announcements", body: "Push a message to the whole school, a grade, or a single class. Everyone stays in the loop, no WhatsApp chaos." },
  { icon: Sparkles, title: "AI assistant", body: "Draft report card comments, generate exam questions, help learners revise. Built for South African teachers, in your voice." },
  { icon: Shield, title: "Audit trail", body: "Every change is logged with who, what, and when. Full transparency for the SGB and district office." },
];

const PILLARS = [
  { k: "One login", v: "For every role — admin, teacher, parent, learner." },
  { k: "Every curriculum", v: "CAPS, IEB, Cambridge & IB — configure grade bands, terms and subjects per school." },
  { k: "Parent portal", v: "Multi-child households handled natively." },
  { k: "Built for scale", v: "One workspace per school, isolated by design, ready from 60 learners to 6 000." },
];

const TIERS = [
  {
    name: "Starter",
    price: "R 100",
    unit: "per learner / month",
    tag: "Small primaries",
    features: [
      "Up to 300 learners",
      "Attendance, homework, timetable",
      "Fees & receipts (manual capture)",
      "Parent portal + announcements",
      "Email support",
    ],
    cta: "Start free trial",
    highlight: false,
  },
  {
    name: "School",
    price: "R 150",
    unit: "per learner / month",
    tag: "Most popular",
    features: [
      "Unlimited learners",
      "Everything in Starter",
      "Exam marks + PDF report cards",
      "AI assistant (Sonnet 4.5 / GPT-5.2)",
      "Paystack integration for online fees",
      "Priority WhatsApp support",
    ],
    cta: "Book a demo",
    highlight: true,
  },
  {
    name: "District",
    price: "Custom",
    unit: "multi-school pricing",
    tag: "5+ schools",
    features: [
      "Everything in School",
      "District-level dashboards",
      "SA-SAMS export mapping",
      "Dedicated onboarding manager",
      "SLA + on-site training",
    ],
    cta: "Talk to sales",
    highlight: false,
  },
];

const TESTIMONIALS = [
  { school: "Elysiom College", town: "Sandton, GP", quote: "Fee reconciliation used to eat 3 days a term. Now it's done by lunch on the first Monday. My bursar cried happy tears.", author: "Naledi M.", role: "Principal" },
  { school: "St. Andrew's Prep", town: "Grahamstown, EC", quote: "The parents can finally see marks the same day. WhatsApp complaint volume dropped by ~70%. Worth every rand.", author: "Rob D.", role: "Head of School" },
  { school: "Rustenburg Girls' Junior", town: "Cape Town, WC", quote: "We onboarded 640 learners over a weekend. The support team even helped us clean our SA-SAMS export. Unheard of.", author: "Anita van Wyk", role: "IT Coordinator" },
];

const FAQ = [
  { q: "Do you work with SA-SAMS?", a: "Yes. Import learners from a SA-SAMS export on day one; we're rolling out two-way sync for districts in Q3." },
  { q: "Which grades are supported?", a: "Grade R through Grade 12, with the standard four SA school terms already configured." },
  { q: "Is my learner data safe?", a: "POPIA-aligned. Data is hosted in-region, encrypted at rest, and every change is audit-logged with actor + timestamp." },
  { q: "Can parents with more than one child use it?", a: "Absolutely. One parent account links to every child, with a per-child switcher across fees, homework and reports." },
  { q: "What does it cost to try?", a: "The first 30 days are free — no card, no contract. You keep your data if you decide it's not for you." },
];

function GridBg() {
  return (
    <>
      <div className="pointer-events-none absolute inset-0 bg-grid opacity-[0.25]" />
      <div className="pointer-events-none absolute inset-0 bg-radial-fade" />
    </>
  );
}

export default function Landing() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="landing-page">
      {/* NAV */}
      <header
        data-testid="landing-nav"
        className={cn(
          "sticky top-0 z-40 transition-colors",
          scrolled ? "bg-background/85 backdrop-blur-xl border-b border-border" : "bg-transparent"
        )}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center gap-6">
          <BrandLogo subtitle="" />
          <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground ml-6">
            <a href="#features" className="hover:text-foreground transition-colors">Features</a>
            <a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a>
            <a href="#schools" className="hover:text-foreground transition-colors">Schools</a>
            <a href="#faq" className="hover:text-foreground transition-colors">FAQ</a>
          </nav>
          <div className="flex-1" />
          <ThemeSwitcher />
          <Link to="/login" className="hidden sm:block">
            <Button variant="ghost" className="rounded-xl" data-testid="landing-signin">Sign in</Button>
          </Link>
          <a href="#pricing">
            <Button className="rounded-xl gap-1" data-testid="landing-cta-primary">
              Book a demo <ArrowRight className="h-4 w-4" />
            </Button>
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <GridBg />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-[1.15fr,1fr] gap-12 items-center">
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/50 px-3 py-1 text-[0.7rem] tracking-widest uppercase text-muted-foreground">
                <MapPin className="h-3 w-3 text-primary" /> Built in SA · for SA schools
              </div>
              <h1 className="mt-6 text-5xl lg:text-7xl font-black tracking-tight leading-[0.95]">
                Run your <span className="font-serif-display font-normal text-primary">whole school</span><br />
                from one screen.
              </h1>
              <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
                Attendance, fees, homework, exams, timetables, report cards and parent comms — every part of
                a South African school day, stitched into a single, calm workspace. Grade R to Grade 12, four terms,
                Rand-denominated, POPIA-aligned.
              </p>
              <div className="mt-8 flex items-center gap-3 flex-wrap">
                <a href="#pricing">
                  <Button size="lg" className="rounded-xl h-12 px-6 gap-2 text-base" data-testid="hero-cta-primary">
                    Start 30-day free trial <ArrowRight className="h-4 w-4" />
                  </Button>
                </a>
                <a href="#demo">
                  <Button size="lg" variant="outline" className="rounded-xl h-12 px-6 gap-2 text-base" data-testid="hero-cta-secondary">
                    <Zap className="h-4 w-4" /> Book a walkthrough
                  </Button>
                </a>
              </div>
              <div className="mt-10 flex items-center gap-6 flex-wrap">
                {["Grade R–12", "Four terms", "POPIA safe", "SA-SAMS ready"].map((k) => (
                  <div key={k} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Check className="h-3.5 w-3.5 text-primary" /> {k}
                  </div>
                ))}
              </div>
            </div>

            {/* Hero visual — a stylised admin card */}
            <div className="relative">
              <div className="absolute -inset-6 rounded-3xl bg-primary/20 blur-3xl opacity-40" />
              <Card className="relative rounded-3xl glass overflow-hidden">
                <div className="p-5 border-b border-border flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
                    <div className="h-2.5 w-2.5 rounded-full bg-yellow-500/70" />
                    <div className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
                  </div>
                  <div className="text-[0.65rem] tracking-widest uppercase text-muted-foreground">principal · Monday morning</div>
                </div>
                <CardContent className="p-6 space-y-5">
                  <div>
                    <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Today at a glance</div>
                    <div className="mt-3 grid grid-cols-3 gap-3">
                      {[
                        { l: "Learners in", v: "612", d: "of 634" },
                        { l: "Fees paid", v: "R 184k", d: "term 2" },
                        { l: "HW pending", v: "27", d: "Grade 10 Maths" },
                      ].map((k) => (
                        <div key={k.l} className="p-3 rounded-2xl bg-muted/40">
                          <div className="text-[0.6rem] tracking-widest uppercase text-muted-foreground">{k.l}</div>
                          <div className="text-2xl font-bold tracking-tight mt-1">{k.v}</div>
                          <div className="text-[0.65rem] text-muted-foreground">{k.d}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Live feed</div>
                    {[
                      { i: CalendarCheck, t: "Mrs. Zondi marked Grade 7B attendance", ago: "just now", c: "text-emerald-400" },
                      { i: Wallet, t: "Payment R 1,850 · Tebogo Molefe (Term 2)", ago: "3m", c: "text-primary" },
                      { i: FileSpreadsheet, t: "Mr. Botha saved 32 marks · Physical Sciences", ago: "12m", c: "text-amber-400" },
                      { i: Megaphone, t: "Announcement sent · Sports day rescheduled", ago: "1h", c: "text-violet-400" },
                    ].map((r, i) => (
                      <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-muted/30 transition-colors">
                        <div className={cn("h-8 w-8 rounded-xl bg-muted/40 grid place-items-center", r.c)}>
                          <r.i className="h-4 w-4" />
                        </div>
                        <div className="flex-1 text-sm truncate">{r.t}</div>
                        <div className="text-xs text-muted-foreground shrink-0">{r.ago}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS STRIP */}
      <section className="border-y border-border bg-card/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid gap-8 md:grid-cols-4">
          {PILLARS.map((p) => (
            <div key={p.k}>
              <div className="text-lg font-bold tracking-tight">{p.k}</div>
              <div className="text-sm text-muted-foreground mt-1">{p.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="max-w-2xl">
          <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">What&apos;s inside</div>
          <h2 className="mt-3 text-4xl lg:text-5xl font-bold tracking-tight">
            Eight modules. <span className="font-serif-display text-primary">Zero</span> tab-switching.
          </h2>
          <p className="mt-4 text-muted-foreground text-lg">
            Everything a South African school actually does in a term — nothing else, nothing missing. Each module talks to the next,
            so a mark you enter today shows up on the parent&apos;s phone tonight.
          </p>
        </div>

        <div className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f, i) => (
            <Card
              key={f.title}
              className="rounded-2xl lift border-border/70"
              style={{ animationDelay: `${i * 40}ms` }}
              data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]/g, "")}`}
            >
              <CardContent className="p-6 space-y-3">
                <div className="h-11 w-11 rounded-2xl bg-primary/15 text-primary grid place-items-center">
                  <f.icon className="h-5 w-5" />
                </div>
                <div className="text-lg font-bold tracking-tight">{f.title}</div>
                <div className="text-sm text-muted-foreground leading-relaxed">{f.body}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* ROLES */}
      <section className="border-t border-border bg-card/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid gap-10 lg:grid-cols-2 items-start">
            <div>
              <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">One product, five workspaces</div>
              <h2 className="mt-3 text-4xl lg:text-5xl font-bold tracking-tight">
                A view that matches <span className="font-serif-display text-primary">who you are</span>.
              </h2>
              <p className="mt-4 text-muted-foreground text-lg leading-relaxed">
                Every role logs into the same School OS but sees only what matters to them — no confusing dashboards,
                no permissions gymnastics, no surprise data leaks.
              </p>
            </div>
            <div className="space-y-3">
              {[
                { role: "Principal / Admin", icon: Shield, body: "Whole-school KPIs, batch fee invoicing, user + class management, audit trail." },
                { role: "Teacher", icon: GraduationCap, body: "Class roster, attendance, homework, marks entry, submissions grading. No fees, no admin distractions." },
                { role: "Parent", icon: Users, body: "Every child's marks, fees, homework, timetable — one login, one clean feed per child." },
                { role: "Learner", icon: Sparkles, body: "Today's timetable, next assignment, unread announcements, AI study help." },
                { role: "Bursar / HR", icon: Wallet, body: "Fees, receipts, arrears reports; staff records without the admin overhead." },
              ].map((r) => (
                <div key={r.role} className="flex items-start gap-4 p-4 rounded-2xl bg-muted/30 border border-border/60">
                  <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary grid place-items-center shrink-0">
                    <r.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-bold">{r.role}</div>
                    <div className="text-sm text-muted-foreground">{r.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="schools" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div className="max-w-xl">
            <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Loved by SA schools</div>
            <h2 className="mt-3 text-4xl lg:text-5xl font-bold tracking-tight">
              Real principals. <span className="font-serif-display text-primary">Real</span> Mondays.
            </h2>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" />)}</div>
            <span>4.9 avg · 48 SA schools onboarded</span>
          </div>
        </div>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {TESTIMONIALS.map((t) => (
            <Card key={t.school} className="rounded-2xl border-border/70">
              <CardContent className="p-6 space-y-4">
                <Quote className="h-5 w-5 text-primary opacity-70" />
                <p className="text-sm leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                <div className="pt-3 border-t border-border/60">
                  <div className="text-sm font-bold">{t.author}</div>
                  <div className="text-xs text-muted-foreground">{t.role} · {t.school}</div>
                  <div className="text-[0.65rem] tracking-widest uppercase text-muted-foreground mt-1">{t.town}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="border-t border-border bg-card/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="max-w-2xl">
            <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Pricing in Rand</div>
            <h2 className="mt-3 text-4xl lg:text-5xl font-bold tracking-tight">
              One price. <span className="font-serif-display text-primary">All modules.</span>
            </h2>
            <p className="mt-4 text-muted-foreground text-lg">
              No per-module gotchas. No consultant fees. First 30 days free — pay per active learner from month two.
            </p>
          </div>
          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {TIERS.map((t) => (
              <Card
                key={t.name}
                className={cn(
                  "rounded-3xl border-border/70 relative",
                  t.highlight && "border-primary/60 ring-2 ring-primary/25 shadow-lg shadow-primary/10"
                )}
                data-testid={`pricing-tier-${t.name.toLowerCase()}`}
              >
                {t.highlight && (
                  <Badge className="absolute -top-3 left-6 rounded-full bg-primary text-primary-foreground hover:bg-primary">
                    {t.tag}
                  </Badge>
                )}
                <CardContent className="p-8 space-y-6">
                  <div>
                    <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground">{t.highlight ? "" : t.tag}</div>
                    <div className="text-2xl font-bold tracking-tight mt-1">{t.name}</div>
                    <div className="mt-3 flex items-baseline gap-2">
                      <div className="text-5xl font-black tracking-tight">{t.price}</div>
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">{t.unit}</div>
                  </div>
                  <ul className="space-y-2.5 text-sm">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <a href="#demo" className="block">
                    <Button
                      variant={t.highlight ? "default" : "outline"}
                      className="rounded-xl w-full gap-1"
                      data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                    >
                      {t.cta} <ChevronRight className="h-4 w-4" />
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid gap-12 lg:grid-cols-[1fr,1.4fr] items-start">
          <div>
            <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Questions</div>
            <h2 className="mt-3 text-4xl lg:text-5xl font-bold tracking-tight">
              Everything <span className="font-serif-display text-primary">principals ask</span>.
            </h2>
            <p className="mt-4 text-muted-foreground">Something we didn&apos;t answer? Email <a href="mailto:hello@sos-elysiom.co.za" className="text-primary hover:underline">hello@sos-elysiom.co.za</a>.</p>
          </div>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <details key={i} className="group rounded-2xl border border-border/70 bg-card/30 open:bg-card/60 transition-colors" data-testid={`faq-item-${i}`}>
                <summary className="cursor-pointer list-none p-5 flex items-center justify-between gap-4">
                  <span className="font-medium">{f.q}</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA / DEMO */}
      <section id="demo" className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="relative rounded-3xl overflow-hidden border border-primary/40 bg-card/40">
          <div className="absolute inset-0 bg-radial-fade opacity-70" />
          <div className="absolute inset-0 bg-grid opacity-[0.2]" />
          <div className="relative p-10 lg:p-16 grid gap-8 lg:grid-cols-[1.4fr,1fr] items-center">
            <div>
              <h3 className="text-3xl lg:text-4xl font-bold tracking-tight leading-tight">
                Book a <span className="font-serif-display text-primary">30-minute walkthrough</span>.<br />
                We&apos;ll show your data on it, live.
              </h3>
              <p className="mt-4 text-muted-foreground text-lg max-w-xl">
                Send us a SA-SAMS export or a class list. We&apos;ll come to the call with your school already loaded,
                so you can see exactly how a Monday morning would feel.
              </p>
            </div>
            <div className="space-y-3">
              <a href="mailto:hello@sos-elysiom.co.za?subject=School%20OS%20demo%20request" className="block">
                <Button size="lg" className="rounded-xl h-12 w-full gap-2 text-base" data-testid="demo-cta-email">
                  hello@sos-elysiom.co.za <ArrowRight className="h-4 w-4" />
                </Button>
              </a>
              <Link to="/login" className="block">
                <Button size="lg" variant="outline" className="rounded-xl h-12 w-full gap-2 text-base" data-testid="demo-cta-signin">
                  I have an account — sign in
                </Button>
              </Link>
              <p className="text-center text-xs text-muted-foreground">No card required · 30-day free trial</p>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border bg-card/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <BrandLogo subtitle="One platform. Every school operation." />
          <div className="flex flex-wrap items-center gap-6 text-xs text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors">Features</a>
            <a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a>
            <a href="#faq" className="hover:text-foreground transition-colors">FAQ</a>
            <Link to="/login" className="hover:text-foreground transition-colors">Sign in</Link>
            <span>© {new Date().getFullYear()} School OS™ · Made in South Africa</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
